import FillMethod from './FillMethod'
import Logger from '../Log'
import {
  ecpmOrDefault,
  firePixel,
  objToQuery,
  renderAd,
  topmostWindowFrame
} from '../utils'
import { ajax } from '../ajax'

const Log = Logger.for('OutbrainFillMethod')

function toStdNative (doc) {
  return {
    mediaType: 'native',
    native: {
      // these vendor links are custom, reqd for OB
      vendorIcon: 'https://ads.smrtb.com/ob_logo_67x12.png',
      vendorClickUrl: 'https://www.outbrain.com/what-is/',
      title: doc.content,
      sponsoredBy: doc.source_display_name,
      clickUrl: doc.url,
      image: {
        url: doc.thumbnail ? doc.thumbnail.url : null
      },
      impressionTrackers: doc.pixels,
      clickTrackers: doc.clickTrackers,
      javascriptTrackers: doc.jsTrackers ? doc.jsTrackers.map(url => '<script src="' + url + '"></script>').join('') : null
    }
  }
}

async function request (params) {
  const frame = topmostWindowFrame()

  const WINDOW_KEY = '_smrtb_ob_t'
  const PENDING = '_pend'
  const t = await new Promise((resolve, reject) => {
    let te = frame[WINDOW_KEY]
    if (te) {
      if (te === PENDING) {
        Log.debug('Someone else has requested an ob t value, waiting for them')

        setTimeout(() => {
          te = frame[WINDOW_KEY]
          if (te === PENDING) {
            Log.debug('First ob delay check, still pending.. we tried')
            resolve(null)
          } else {
            Log.debug('OB delay check got result on first poll')
            return resolve(te)
          }
        }, 200)
      } else {
        Log.debug('Found existing and ready outbrain t value')
        return resolve(te)
      }
    } else {
      Log.debug('No outbrain t value found yet')
      frame[WINDOW_KEY] = PENDING

      resolve(null)
    }
  })

  params.t = t

  const url = 'https://odb.outbrain.com/utils/platforms?' + objToQuery(params)
  Log.debug('Outbrain query: ' + url)

  const data = await ajax(url, null, true)
  if (!t) {
    Log.info('Setting new T value ' + data.response.request.t)
    frame[WINDOW_KEY] = data.response.request.t
  }

  return data
}

export default class OutbrainFillMethod extends FillMethod {
  constructor (bidder) {
    super('outbrain', bidder, 'native', ecpmOrDefault(bidder.params.ecpm, 0.3))
  }

  fill (slot, media) {
    return new Promise((resolve, reject) => {
      request(this._bidder.params).then(data => {
        if (!data || !data.response || !data.response.documents || !data.response.documents.doc.length) {
          Log.debug('No outbrain bids returned')
          return resolve({ cpm: 0 })
        }

        const docs = data.response.documents.doc
        Log.debug('Got ' + docs.length + ' units back from Outbrain')

        const pixels = [data.response.viewability_actions.reportViewed]

        let rpm = 0
        for (const doc of docs) {
          Log.debug('Rendering an ob doc for ' + doc.source_name)
          renderAd(toStdNative(doc), slot, media)
          rpm += parseFloat(doc.predictedRpm)
          pixels.push(...doc['on-viewed'])
        }

        if ('IntersectionObserver' in window) {
          Log.debug('Attaching viewability monitors')

          /* global IntersectionObserver */
          const io = new IntersectionObserver(e => {
            const res = e.filter(e => e.isIntersecting && e.target === slot.node())
            if (res.length === 0) {
              return Log.debug('Nonintersecting IO event or for different element')
            }

            /* fire all at once for now until we refactor access to individual sub nodes */
            Log.debug('Firing and forgetting viewability pixels')

            pixels.forEach(pixel => {
              Log.debug('Firing viewability pixel ' + pixel)
              firePixel(pixel)
            })

            io.disconnect()
          }, { threshold: 0.5 })

          io.observe(slot.node())
        }

        firePixel(data.response.viewability_actions.reportServed)
        Log.debug('Fired imp pixel')

        resolve({
          cpm: rpm || 0.25,
          width: 1,
          height: 1
        })
      })
    })
  }
}
